
import { Event } from 'main.core';
import store from '../store';
import { ActionCreators } from '../store/actions';
import getScrollbarWidth from '../utils/getScrollbarWidth';

async function injectContent(content)
{
	const assets = BX.processHTML(content);
	BX.loadCSS(assets.STYLE);
	if (assets.SCRIPT)
	{
		await new Promise(resolve => {
			BX.ajax.processScripts(assets.SCRIPT, false, resolve)
		});
	}
	return true;
}

async function injectAssets(assets)
{
	if (assets.css)
	{
		BX.loadCSS(assets.css);
	}

	if (assets.js)
	{
		await Promise.all(
			assets.js.map(src => new Promise((resolve, reject) => {
				BX.loadScript(src, resolve)
			}))
		);
	}

	if (assets.string)
	{
		await injectContent(assets.string.toString())
	}

	return true;
}

const Registry = new Map();
let idsIterator = 0;

class Modal extends Event.EventEmitter
{
	options = {

		modal: undefined,

		templates: {

			backdrop: `<div class="modal-backdrop fade"></div>`,

			loader: `
			<div class="modal">
				<div class="modal-dialog modal-dialog-centered justify-content-center">
					<div class="inline-flex bg-dark rounded p-2">
						<div class="spinner-border text-light" role="status">
							<span class="sr-only">Loading...</span>
						</div>
					</div>
				</div>
			</div>
			`,

			modal: `
				<div class="modal fade" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
					<div class="modal-dialog  modal-dialog-centered">
						<div class="modal-content">
							<div class="modal-header">
								<h3 class="modal-title"></h3>
								<button type="button" class="close" data-dismiss="modal">
									<svg class="icon-svg" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
										<path d="M0 0h24v24H0z" fill="none"/><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
									</svg>
								</button>
							</div>
							<div class="modal-body">

							</div>
						</div>
					</div>
				</div>
			`
		},

		cssClasses: {
			header: `modal-header`,
			dialog: `modal-dialog`,
			title: `modal-title`,
			content: `modal-content`,
			body: `modal-body`
		}

	};

	#modal = undefined;
	#loader = undefined;
	#backdrop = undefined;
	#placeholder = undefined;

	#title = '';
	#body = '';

	#id = false;
	#ignoreDismiss = false;

	static get Events()
	{
		return Object.freeze({
			SHOW: 'show',
			SHOWN: 'shown',
			HIDE: 'hide',
			HIDDEN: 'hidden',
			AFTER_LOADED: 'afterLoaded',
			AFTER_INSERTED: 'afterInserted'
		});
	}

	static load(url, options = {}, data = {})
	{
		const { method, title, extendOptions } = options;

		const instance = new Modal(extendOptions || {});
		instance.title = title;

		instance
			.addBackdrop()
			.startLoader();

		(async function () {
			try
			{
				const requestData = await new Promise((resolve, reject) => {
					const onsuccess = resolve;
					const onfailure = reject;
					BX.ajax({ url, method, data, onsuccess, onfailure })
				});

				const eventData = { instance, requestData };
				instance.emit(Modal.Events.AFTER_LOADED, new Event.BaseEvent({ data: { instance, requestData } }));

				const jsonData = BX.parseJSON(eventData.requestData);
				if (jsonData)
				{
					if ('status' in jsonData && 'data' in jsonData)
					{
						if (jsonData.status === 'success')
						{
							if ((jsonData.data.assets))
							{
								await injectAssets(jsonData.data.assets);
							}

							instance.body = jsonData.data.html;
						}
					}
					else
					{
						if (jsonData.STYLE)
							await injectContent(jsonData.STYLE);

						if (jsonData.JS)
							await injectContent(jsonData.JS);

						instance.body = jsonData.DATA;
					}
				}
				else
					instance.body = eventData.requestData;

				await injectContent(instance.body.innerHTML);

				instance.emit(Modal.Events.AFTER_INSERTED, new Event.BaseEvent({ data: { instance } }));
			}
			catch(e)
			{
				console.error(e)
			}
			finally
			{
				instance.subscribe(Modal.Events.SHOW, () => {
					instance.endLoader();
				});

				instance.show();
			}
		}());

		return instance;
	}

	static getInstanceById(id)
	{
		return Registry.get(id);
	}

	static open(options)
	{
		const { title, content, extendOptions } = options;
		const instance = new Modal(extendOptions || {});

		instance.title = title;
		instance.body = content;

		instance.show();

		return instance;
	}

	constructor(options = {})
	{
		super();

		this.setEventNamespace('EShop24.Modal');

		this.#id = 'modal_' + (++idsIterator);
		this.options = {
			...this.options,
			templates: {
				...this.options.templates,
				...(options.templates || {})
			},
			cssClasses: {
				...this.options.cssClasses,
				...(options.cssClasses || {})
			}
		};

		this.init();
	}

	get element()
	{
		return this.#modal;
	}

	get title()
	{
		return this.#title;
	}

	set title(val)
	{
		if (this.#title === val)
			return;

		this.#title = val || '';

		if (this.#title.length)
		{
			let titleNode = this.#modal.querySelector(`.${this.options.cssClasses.title}`);
			if (titleNode)
			{
				titleNode.innerHTML = this.#title;
			}
			else
			{
				titleNode = document.createElement('div');
				titleNode.classList.add(this.options.cssClasses.header);

				const headerNode = this.#modal.querySelector(`.${this.options.cssClasses.header}`)
				if (headerNode)
					headerNode.insertBefore(titleNode, headerNode.firstChild)
			}
		}
		else
		{
			let titleNode = this.#modal.querySelector(`.${this.options.cssClasses.title}`);
			if (titleNode)
				titleNode.remove();
		}
	}

	get body()
	{
		return this.#modal.querySelector(`.${this.options.cssClasses.body}`);
	}

	set body(val)
	{

		const bodyNode = this.#modal.querySelector(`.${this.options.cssClasses.body}`);
		if (bodyNode)
		{
			if (!this.#body.nodeType)
			{
				this.#body = val || '';
				bodyNode.innerHTML = this.#body;
			}
			else if (this.isShowed())
			{
				this.releasePlaceholder();

				this.#body = val || '';

				bodyNode.innerHTML = '';
				bodyNode.appendChild(this.#body);
			}
		}
	}

	init()
	{
		Registry.set(this.#id, this);

		if(this.options.modal)
			this.#modal = modal;
		else
			this.createModal();

		this.bindEvents();
	}

	createModal()
	{
		const container = document.createElement('div');
		container.innerHTML = this.options.templates.modal;
		this.#modal = container.children[0];
		document.body.appendChild(this.#modal);
	}

	bindEvents()
	{
		$(this.#modal).on('hidden.bs.modal', () => {
			this.emit(Modal.Events.HIDDEN, new Event.BaseEvent({ data: { instance: this }}));
		});

		$(this.#modal).on('hide.bs.modal', () => {
			this.emit(Modal.Events.HIDE, new Event.BaseEvent({ data: { instance: this }}));
		});
		$(this.#modal).on('show.bs.modal', () => {
			this.emit(Modal.Events.SHOW, new Event.BaseEvent({ data: { instance: this }}));
		});

		$(this.#modal).on('shown.bs.modal', () => {
			this.emit(Modal.Events.SHOWN, new Event.BaseEvent({ data: { instance: this }}));
		});

		const dialog = this.#modal.querySelector(`.${this.options.cssClasses.dialog}`);
		if (dialog)
		{
			$(dialog).on('click.dismiss', () => { this.#ignoreDismiss = true; });
			$(this.#modal).on('click.dismiss', this.triggerDismiss.bind(this));
		}

		this.subscribe(Modal.Events.SHOW, () => {
			store.dispatch(ActionCreators.uiSetActiveModal(this.#id));
		});

		this.subscribe(Modal.Events.HIDDEN, () => {
			store.dispatch(ActionCreators.uiSetActiveModal(null));
		});

		this.subscribe(Modal.Events.SHOW, () => {
			if (!this.#body || !this.#body.nodeType)
				return;

			const bodyNode = this.#modal.querySelector(`.${this.options.cssClasses.body}`);
			if (!bodyNode)
				return;

			this.addPlaceholder();

			bodyNode.innerHTML = '';
			bodyNode.appendChild(this.#body);
		});

		this.subscribe(Modal.Events.HIDDEN, () => {
			requestAnimationFrame(() => {
				if(store.state.ui.window.fixedScrollbar)
					document.body.style.paddingRight = getScrollbarWidth() + 'px';
			});
		});
	}

	unbindEvents()
	{
		$(this.#modal).off('hidden.bs.modal');
		$(this.#modal).off('hide.bs.modal');
		$(this.#modal).off('show.bs.modal');
		$(this.#modal).off('shown.bs.modal');

		const dialog = this.#modal.querySelector(`.${this.options.cssClasses.dialog}`);
		if (dialog)
		{
			$(dialog).off('click.dismiss');
			$(this.#modal).off('click.dismiss')
		}
	}


	disable(spinner = true)
	{
		const content = this.#modal.querySelector(`.${this.options.cssClasses.content}`);
		if (content)
		{
			const element = document.createElement('div');
			element.classList.add('modal-disable');
			content.appendChild(element);

			if (spinner)
			{
				element.innerHTML = `
					<div class="spinner-border text-primary" role="status">
						<span class="sr-only">Loading</span>
					</div>
				`
			}
		}
	}

	enable()
	{
		const element = this.#modal.querySelector(`.modal-disable`);
		if (element)
			element.remove();
	}

	startLoader()
	{
		if (this.#loader)
			return;

		const container = document.createElement('div');
		container.innerHTML = this.options.templates.loader;

		this.#loader = container.children[0];

		document.body.appendChild(this.#loader);

		$(this.#loader).modal({ backdrop: false });
		$(this.#loader).on('hidden.bs.modal', () => {
			if(store.state.ui.window.fixedScrollbar)
				document.body.style.paddingRight = getScrollbarWidth() + 'px';
		});

		return this;
	}

	endLoader()
	{
		if (!this.#loader)
			return;

		$(this.#loader).modal('hide');
		this.#loader.remove();
		this.#loader = null;

		return this;
	}

	triggerDismiss()
	{
		if (this.#ignoreDismiss)
		{
			this.#ignoreDismiss = false;
			return;
		}

		this.hide();
	}

	addBackdrop()
	{
		if (this.#backdrop)
			return this;

		const container = document.createElement('div');
		container.innerHTML = this.options.templates.backdrop;

		this.#backdrop = container.children[0];

		this.subscribe(Modal.Events.HIDDEN, () => {
			this.releaseBackdrop();
		});

		this.#backdrop.addEventListener('click', this.triggerDismiss.bind(this));

		document.body.appendChild(this.#backdrop);
		setTimeout(() => {
			this.#backdrop.classList.add('show');
		});

		return this;
	}

	releaseBackdrop()
	{
		if (!this.#backdrop)
			return;

		this.#backdrop.classList.remove('show');
		this.#backdrop.addEventListener('transitionend', () => {
			if (this.#backdrop)
			{
				this.#backdrop.remove();
				this.#backdrop = undefined;
			}
		});

		return this;
	}

	addPlaceholder()
	{
		if (!this.#body.nodeType || !this.#body.parentNode)
			return;

		this.#placeholder = document.createElement('div');
		this.#body.parentNode.insertBefore(this.#placeholder, this.#body);

		this.subscribeOnce(Modal.Events.HIDDEN, () => {
			this.releasePlaceholder()
		});
	}

	releasePlaceholder()
	{
		if (!this.#placeholder)
			return;

		this.#placeholder.parentNode.insertBefore(this.#body, this.#placeholder);
		this.#placeholder.remove();
	}

	show()
	{
		this.addBackdrop();

		$(this.#modal).modal({
			backdrop: false
		});

		return this;
	}

	hide()
	{
		$(this.#modal).modal('hide');

		return this;
	}

	dispose()
	{
		this.unbindEvents();
		this.unsubscribeAll();
		this.endLoader();
		this.releaseBackdrop();
		this.#modal.remove();

		Registry.delete(this.#id)
	}

	isShowed ()
	{
		return store.state.ui.modal.activeId == this.#id;
	}
}

export default Modal;